import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Alert, alpha, Box, CircularProgress, Container, IconButton, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { getDocById } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import { BusinessSpecification } from '../../../../MicrospecDynamicUI/Models/Business/BusinessSpecification';
import { useMicrospecThemeLanguageContext } from '../../../../MicrospecDynamicUI/Themes';
import { SPECIFICATIONS_COLLECTION } from '../../../constants/collectionPaths';
import UpsertSpecification from './UpsertSpecification';

const UpdateSpecification: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { translate } = useMicrospecThemeLanguageContext().localizationProvider;
  const { specificationId } = useParams<{ specificationId: string }>();
  const db = useMicrospecDynamicUIContext().cloudProvider.firestore?.instance;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [item, setItem] = useState<BusinessSpecification | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!db || !specificationId) { setError(translate('specification.missingParameters')); setLoading(false); return; }
      try {
        const data = await getDocById(null, null, db, SPECIFICATIONS_COLLECTION, specificationId, false);
        if (data) setItem(data as BusinessSpecification);
        else setError(translate('specification.notFound'));
      } catch (err: any) {
        setError(err.message || translate('specification.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [db, specificationId, translate]);

  const handleCancel = () => navigate('/manager/specifications/manage');

  if (loading) return <Container maxWidth="lg" sx={{ py: 4 }}><Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box></Container>;
  if (error) return <Container maxWidth="lg" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!item) return <Container maxWidth="lg" sx={{ py: 4 }}><Alert severity="warning">{translate('specification.notFound')}</Alert></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={handleCancel} sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1), '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) } }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900] }}>
            {translate('specification.editTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">{translate('specification.updateDescription')}</Typography>
        </Box>
      </Box>
      <UpsertSpecification
        specificationId={specificationId}
        onSave={() => { window.location.href = '/manager/specifications/manage'; }}
        onCancel={handleCancel}
      />
    </Container>
  );
};

export default UpdateSpecification;
